import React, { useState } from 'react'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import SVG from 'react-inlinesvg'

import Layout from '../Layout'
import SEO from '../components/SEO'
import OneColumn from '../components/OneColumn'
import Popup from '../components/Popup'
import Button from '../components/Button'
import Accordion from '../components/Accordion'

import Paragraph from '../components/Paragraph'

import Redeem from '../sections/Redeem'

import enjoyPic from '../assets/images/frequent-diner/enjoy.jpg'
import earnPic from '../assets/images/frequent-diner/earn.jpg'
import redeemPic from '../assets/images/frequent-diner/redeem.jpg'
import videoPlaceholderPic from '../assets/images/frequent-diner/video-placeholder.jpg'

const StyledThreeColumns = styled.div`
  width: 100%;

  ${({ theme }) => theme.media.l} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 32px;
  }

  ${({ theme }) => theme.media.xl} {
    grid-column-gap: 45px;
  }

  ${({ theme }) => theme.media.xxl} {
    grid-column-gap: 60px;
  }
`

const StyledTwoColumns = styled.div`
  width: 100%;
  margin: 80px 0;

  ${({ theme }) => theme.media.l} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 32px;
  }

  ${({ theme }) => theme.media.xl} {
    grid-column-gap: 45px;
  }

  ${({ theme }) => theme.media.xxl} {
    grid-column-gap: 60px;
  }
`

const StyledItem = styled.div`
  h2 {
    margin: 0 0 8px;
    font-size: 38px;
    font-weight: ${({ theme }) => theme.font.bold};
    line-height: 1;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const StyledImage = styled.div`
  margin: 0 0 24px;
  width: 100%;
  height: 300px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: center center url(${({ image }) => image}) no-repeat;
  background-size: cover;
`

const StyledRegisterButton = styled.a`
  display: block;
  width: calc(100% + 120px);
  margin: 40px -60px;
  padding: 20px 30px;
  font-size: 20px;
  font-weight: ${({ theme }) => theme.font.bold};
  text-align: Center;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: $white;
  cursor: pointer;

  ${({ theme }) => theme.media.l} {
    width: calc(100% + 1000px);
    margin: 80px -500px;
    padding: 20px 0;
  }
`

const StyledVideo = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% + 1000px);
  height: 600px;
  margin: 20px -500px;
  background: center center url(${({ placeholder }) => placeholder}) no-repeat;
  background-size: cover;
  color: $white;

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: #a78d66;
    cursor: pointer;

    &::before {
      content: '';
      width: 0;
      height: 0;
      margin-left: 5px;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-left: 15px solid ${({ theme }) => theme.colors.white};
    }
  }

  ${({ theme }) => theme.media.l} {
    margin: 40px -500px;
  }
`

const RestaurantsLogoList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`

const RestaurantsLogoItem = styled.li`
  position: relative;
  width: 50%;
  margin: 0;
  padding: 0 20px;
  cursor: pointer;

  svg {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
  }

  ${({ theme }) => theme.media.m} {
    width: 33%;
  }

  ${({ theme }) => theme.media.l} {
    width: 20%;
  }
`

const FrequentDinerPage = ({ data }) => {


  const Icon = ({ filename }) => {
    const req = require.context(`../assets/svg/restaurants`, true, /\.svg$/)
    const IconTag = req(`./${filename}.svg`)

    return <SVG src={IconTag} />
  }

  return (
    <Layout>
      <SEO title="S&S Hospitality | Elite Club" canonical="https://www.sandshospitality.com/loyalty/" description='We reward your loyalty! Join S&S Elite Club and access member benefits. Earn points with every visit and redeem for cash voucher, birthday privilege, special discounts, VIP events. ' />
      <OneColumn>
        <br /><br /><br />
        <h1 class="page-header">The S&S Elite Club</h1>
        <Paragraph>
        The S&S Elite Club offers you exclusive benefits and rewards.
        </Paragraph>
        <br /><br /><br />
        <div>
          <Button><a href="https://member.sandshospitality.com/" target="blank">Join the Club</a></Button>
          <Button><a href="https://member.sandshospitality.com/" target="blank">Login</a></Button>
        </div>
        <br /><br /><br />
        <Paragraph>
          Offering value, choice & priority, the S&S Elite membership brings you a whole new world of dining and hospitality experiences – opportunities to not just enjoy great food, but to participate in the extraordinary evolution of creative cuisine. 
        </Paragraph>
        <div>
        <Accordion
          title="Your Reason to Join"
          content="A unique club for lovers of extraordinary food and entertainment, the S&S Elite Club offers members access, choice & value across our growing range of fine restaurants, specialty bars and designer lounges. Click on the following headlines to explore more!"
        />
        <Accordion
          title="Your Gems"
          content="
            <p>Dazzling blood-red Rubies and the night-sky Sapphires symbolise each point (Gem) you collect as you spend with S&S Hospitality. Collecting these Gems is fun – especially when you know what treasures they bring.</p>
            <br />
            <p>•	In-House Dining: Members are awarded 1 Gem for each HK$1 spent for any dine-in purchase at any restaurant, bar or terrace</p>
            <p>•	Takeaway: Members are awarded 1 Gem for every HK$2 spent on any takeaway purchase at any of restaurant</p>
          "
        />
        <Accordion
          title="Rewards – ‘Cashing In’ Your Gems!"
          content="
            <p>Your precious Gems bring you unbeatable value when you redeem them for exciting, precious rewards like: <br /><br />
            •	Private room tasting menu: Meet & Greet with the Head Chef at one of our exciting restaurants to enjoy a multiple-course tasting menu with an amazing view and interiors <br />
            •	Private masterclass: Learn the craft of cocktail making from one of our talented bartenders  <br />
            •	Sweet treat: Choice of delicious desserts  <br />
            •	Cocktails: With an amazing view or most design-led interiors – the perfect ambience to enjoy a sip of drink-making brilliance – delicately mixed or stirred by our talented mixologists <br />
            •	Private cooking classes: Hosted by one of our celebrated Head Chefs<br />
            •	Dining vouchers: Worth HK$500 or HK$1000 to be redeemed like cash at any outlet<br />
            •	Free lunches: Enjoy an upscale 2- to 3-course lunch or sumptuous buffets <br />
            </p>            
          "
        />
        </div>
        
      </OneColumn>
    </Layout>
  )
}

export default FrequentDinerPage

export const pageQuery = graphql`
  query LoyaltyQuery {
    allStrapiRestaurants(
      filter: { frequentDiner: { eq: true } }
      sort: { fields: [Order], order: ASC }
    ) {
      edges {
        node {
          id
          Order
          slug
          published
          Name
          frequentDiner
          logo {
            absolutePath
            relativePath
            base
            extension
            childImageSharp {
              fluid {
                src
              }
            }
          }
        }
      }
    }
    allStrapiRewards {
      edges {
        node {
          id
          name
          image {
            childImageSharp {
              fluid {
                src
              }
            }
          }
          points
        }
      }
    }
  }
`
